import React, { useState } from "react";
import { X } from "lucide-react";
import { INCOTERM_DEF, STAGES_DEF } from "./constants";

export default function IncotermsModal({ onClose }) {
    const [active, setActive] = useState(null);

    return (
        <div className="fixed inset-0 flex items-center justify-center p-4"
            style={{background:"rgba(0,0,0,0.80)", zIndex:9999}}>
            <div className="bg-[#111827] border border-[#1F2937] rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#1F2937] shrink-0">
                    <div>
                        <h2 className="text-base font-bold text-white">Incoterms 2020 — Quick Reference</h2>
                        <p className="text-[11px] text-slate-500 mt-0.5">Click any row for details. Grey = seller's cost. Blue = buyer's cost. ◆ = risk transfer.</p>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-lg bg-[#1F2937] flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
                        <X size={15}/>
                    </button>
                </div>

                <div className="overflow-auto flex-1 p-4">
                    {/* Stage header */}
                    <div className="flex gap-1 mb-2 min-w-[640px]">
                        <div className="w-28 shrink-0"/>
                        <div className="w-40 shrink-0 text-[10px] text-slate-500 pl-2">Full Name</div>
                        <div className="flex-1 flex gap-0.5">
                            {STAGES_DEF.map((s, i) => (
                                <div key={i} className="flex-1 flex flex-col items-center text-center">
                                    <span className="text-base leading-none">{s.icon}</span>
                                    <span className="text-[9px] text-slate-500 mt-0.5 leading-tight">{s.label}</span>
                                </div>
                            ))}
                        </div>
                        <div className="w-32 shrink-0 text-[10px] text-slate-500 text-right">Risk Transfer Point</div>
                    </div>

                    {/* Incoterm rows */}
                    <div className="space-y-1 min-w-[640px]">
                        {INCOTERM_DEF.map(term => (
                            <div key={term.code}>
                                <button
                                    className={`w-full flex gap-1 items-center rounded-lg px-1 py-1.5 transition-colors text-left ${active === term.code ? "bg-[#1F2937]" : "hover:bg-[#1A2030]"}`}
                                    onClick={() => setActive(active === term.code ? null : term.code)}
                                >
                                    {/* Code badge */}
                                    <div className="w-28 shrink-0 flex items-center gap-2">
                                        <span className="px-2 py-0.5 rounded text-[11px] font-black bg-[#1F2937] text-blue-400 border border-blue-500/30 tracking-wider">
                                            {term.code}
                                        </span>
                                        <span className="text-[9px] text-slate-600 uppercase">{term.tag}</span>
                                    </div>
                                    {/* Full name */}
                                    <div className="w-40 shrink-0 text-[11px] text-slate-300 pr-2 truncate">{term.name}</div>
                                    {/* Visual bar */}
                                    <div className="flex-1 flex gap-0.5">
                                        {STAGES_DEF.map((_, i) => {
                                            const isSeller = i <= term.sellerEnd;
                                            const isRisk   = i === term.riskAt;
                                            return (
                                                <div key={i} className="flex-1 relative flex items-center justify-center" style={{height:22}}>
                                                    <div className="absolute inset-0 rounded-sm" style={{
                                                        background: isSeller ? "#374151" : "#1E3A5F",
                                                        opacity: isSeller ? 1 : 0.6,
                                                    }}/>
                                                    {isRisk && (
                                                        <span className="relative z-10 text-[13px] leading-none" style={{color:"#F59E0B"}}>◆</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* Risk point label */}
                                    <div className="w-32 shrink-0 text-right text-[10px] text-slate-400 pl-2 truncate">{term.where}</div>
                                </button>
                                {/* Expandable description */}
                                {active === term.code && (
                                    <div className="mx-1 mb-1 px-3 py-2.5 bg-[#0D1520] border border-[#1F2937] rounded-lg text-[11px] text-slate-300 leading-relaxed">
                                        <span className="font-bold text-white mr-2">{term.code} — {term.name}</span>
                                        {term.desc}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Legend */}
                    <div className="flex items-center gap-6 mt-4 pt-3 border-t border-[#1F2937] text-[11px] text-slate-400">
                        <span className="flex items-center gap-2">
                            <span className="w-12 h-4 rounded-sm bg-[#374151] inline-block"/>
                            Seller's obligation
                        </span>
                        <span className="flex items-center gap-2">
                            <span className="w-12 h-4 rounded-sm bg-[#1E3A5F] inline-block opacity-60"/>
                            Buyer's obligation
                        </span>
                        <span className="flex items-center gap-2">
                            <span className="text-amber-400 text-base">◆</span>
                            Transfer of risk
                        </span>
                        <span className="ml-auto text-slate-600">Incoterms® 2020 — ICC Publication</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
